/**
 * MusicController: binds the MusicPlayer core to an HTMLAudioElement routed through the audio
 * engine's music bus, keeps preferences in sync, and exposes a snapshot for the player UI.
 */
import { AudioEngine } from '../framework/audio/AudioEngine';
import { MusicPlayer, type MediaAdapter, type PlayerSnapshot, type TrackInfo } from '../framework/audio/musicPlayer';
import type { Playlist } from '../framework/world/manifest';
import { assetUrl, ASSETS_CROSS_ORIGIN } from './assets';
import type { PrefsStore } from './prefs';

class ElementMedia implements MediaAdapter {
  readonly el: HTMLAudioElement;
  onEnded: (() => void) | null = null;
  onError: (() => void) | null = null;
  constructor() {
    this.el = new Audio();
    this.el.preload = 'metadata';
    // Required for R2/CDN-hosted audio routed through Web Audio (otherwise output is silent).
    if (ASSETS_CROSS_ORIGIN) this.el.crossOrigin = 'anonymous';
    this.el.addEventListener('ended', () => this.onEnded?.());
    this.el.addEventListener('error', () => this.onError?.());
  }
  load(url: string) {
    this.el.src = url;
    this.el.load();
  }
  play() {
    return this.el.play();
  }
  pause() {
    this.el.pause();
  }
  seek(s: number) {
    this.el.currentTime = s;
  }
  get currentTime() {
    return this.el.currentTime;
  }
  get duration() {
    return this.el.duration;
  }
}

type Listener = () => void;

export class MusicController {
  readonly player: MusicPlayer;
  private media: ElementMedia;
  private snap: PlayerSnapshot;
  private listeners = new Set<Listener>();
  private connected = false;
  private worldId = '';
  private hiddenPaused = false;

  constructor(
    private audio: AudioEngine,
    private prefs: PrefsStore,
  ) {
    this.media = new ElementMedia();
    this.player = new MusicPlayer(
      this.media,
      { setTimeout: (fn, ms) => window.setTimeout(fn, ms), clearTimeout: (h) => window.clearTimeout(h as number), now: () => Date.now() },
      Math.random,
      {
        fade: (target, s) => audio.setFade(target, s),
        onChange: (s) => {
          this.snap = s;
          this.listeners.forEach((fn) => fn());
        },
      },
    );
    const p = prefs.get().audio;
    this.player.setAmbientBreaks(p.ambientBreaks);
    this.player.setShuffle(p.shuffle);
    this.player.setRepeat(p.repeat);
    this.snap = this.player.snapshot();
    this.applyLevels();
    prefs.subscribe(() => this.syncPrefs());
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): PlayerSnapshot => this.snap;

  get element(): HTMLAudioElement {
    return this.media.el;
  }

  private syncPrefs(): void {
    const p = this.prefs.get().audio;
    const s = this.player.snapshot();
    if (s.ambientBreaks !== p.ambientBreaks) this.player.setAmbientBreaks(p.ambientBreaks);
    if (s.shuffle !== p.shuffle) this.player.setShuffle(p.shuffle);
    if (s.repeat !== p.repeat) this.player.setRepeat(p.repeat);
    this.applyLevels();
  }

  private applyLevels(): void {
    const p = this.prefs.get().audio;
    this.audio.setMusicLevels({ volume: p.musicVolume, muted: p.musicMuted });
    this.audio.setEnvLevels({ volume: p.envVolume, muted: p.envMuted });
  }

  /** World changes suggest a playlist; listener intent (keep current music) wins. */
  setPlaylist(pl: Playlist, worldId: string, fromWorldLoad: boolean): void {
    const tracks: TrackInfo[] = pl.tracks.map((t) => ({ id: t.id, title: t.title, artist: t.artist, url: assetUrl(t.sources[0].url), source: t.source, license: t.license }));
    if (this.worldId === worldId && fromWorldLoad) return;
    const keep = this.worldId !== '' && this.prefs.get().audio.keepMusicOnWorldChange;
    this.worldId = worldId;
    this.player.setPlaylist(tracks, { keepCurrent: keep });
  }

  /**
   * Must run inside a user gesture. Everything happens synchronously within the gesture (context
   * creation, element wiring, play()) so strict autoplay policies (iOS Safari) accept it.
   */
  play(): void {
    this.ensureAudio();
    this.player.play();
  }

  ensureAudio(): void {
    void this.audio.unlock();
    const ctx = this.audio.context;
    const input = this.audio.musicInput;
    if (!this.connected && ctx && input) {
      try {
        const src = ctx.createMediaElementSource(this.media.el);
        src.connect(input);
        this.connected = true;
      } catch {
        /* already connected or unsupported: element still plays directly */
      }
    }
  }

  toggle(): void {
    const s = this.player.snapshot().status;
    if (s === 'playing' || s === 'loading' || s === 'break') this.player.pause();
    else this.play();
  }

  pauseForHidden(): void {
    const s = this.player.snapshot().status;
    if (s === 'playing' || s === 'loading') {
      this.hiddenPaused = true;
      this.player.pause();
    }
  }

  resumeFromHidden(): void {
    if (this.hiddenPaused) {
      this.hiddenPaused = false;
      this.player.play();
    }
  }

  currentTime(): number {
    return this.media.el.currentTime || 0;
  }

  duration(): number {
    const d = this.media.el.duration;
    return Number.isFinite(d) ? d : 0;
  }

  dispose(): void {
    this.player.dispose();
    this.media.el.removeAttribute('src');
  }
}
